import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from '@angular/fire/auth';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/** Minimal session-relevant projection of a Firebase user. */
export interface AuthUser {
  readonly uid: string;
  readonly email: string;
}

function toAuthUser(user: User): AuthUser {
  return { uid: user.uid, email: user.email ?? '' };
}

/**
 * Low-level wrapper over the Identity Platform / Firebase Auth SDK.
 *
 * Only thin SDK calls live here — no app state. The NgRx auth slice is the
 * single source of truth for session state; effects orchestrate these calls.
 *
 * These methods are invoked from effects/interceptors (outside Angular's
 * injection context), so every `@angular/fire` SDK call is wrapped in
 * `runInInjectionContext` — otherwise @angular/fire warns that calling Firebase
 * APIs outside an injection context can destabilise change detection.
 */
@Injectable({ providedIn: 'root' })
export class AuthApi {
  private readonly auth = inject(Auth);
  private readonly injector = inject(Injector);

  /** Point credential validation at a tenant's user pool. Must be set before sign-in. */
  setTenant(tenantId: string | null): void {
    this.auth.tenantId = tenantId;
  }

  async signIn(email: string, password: string): Promise<AuthUser> {
    const credential = await runInInjectionContext(this.injector, () =>
      signInWithEmailAndPassword(this.auth, email, password),
    );
    return toAuthUser(credential.user);
  }

  /**
   * Google sign-in (the only exposed login path). The active tenant
   * must already be set on `this.auth` (see setTenant). The optional `hd`
   * param (environment.googleHostedDomainHint) limits the account picker to
   * one Google Workspace — a UI hint only; the blocking function does the
   * real enforcement (allowed domains + AdminInvites) server-side.
   */
  async signInWithGoogle(): Promise<AuthUser> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      ...(environment.googleHostedDomainHint
        ? { hd: environment.googleHostedDomainHint }
        : {}),
      prompt: 'select_account',
    });
    const credential = await runInInjectionContext(this.injector, () =>
      signInWithPopup(this.auth, provider),
    );
    return toAuthUser(credential.user);
  }

  signOut(): Promise<void> {
    return runInInjectionContext(this.injector, () => signOut(this.auth));
  }

  /** Current ID token (JWT), refreshed via the SDK rather than cached. */
  getIdToken(forceRefresh = false): Promise<string | null> {
    const user = this.auth.currentUser;
    return user ? user.getIdToken(forceRefresh) : Promise.resolve(null);
  }

  /**
   * Resolves once Firebase has settled the persisted session on startup,
   * yielding the restored user (or null). Used by `Restore Session`.
   */
  resolveCurrentUser(): Promise<AuthUser | null> {
    return new Promise((resolve) => {
      const unsubscribe = runInInjectionContext(this.injector, () =>
        onIdTokenChanged(this.auth, (user) => {
          unsubscribe();
          resolve(user ? toAuthUser(user) : null);
        }),
      );
    });
  }

  /** Stream of token changes (sign-in / refresh / sign-out). */
  idTokenChanges(): Observable<AuthUser | null> {
    return new Observable<AuthUser | null>((subscriber) => {
      const unsubscribe = runInInjectionContext(this.injector, () =>
        onIdTokenChanged(
          this.auth,
          (user) => subscriber.next(user ? toAuthUser(user) : null),
          (error) => subscriber.error(error),
        ),
      );
      return unsubscribe;
    });
  }
}
